import React, { useEffect, useState } from 'react';
import { Form, Input, Spin, message } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import Text from 'components/Text';
import useGetScreenWaiting from '../../services/hooks/useGetScreenWaiting';
import { MediaKindType } from '../../services/types';
import accountApis from 'services/accountApis';
import { Row } from 'components/Box/Row';
import colorTheme from 'constant/colors';

type WaitingMediaFormValues = {
  media: {
    id?: string;
    url: string;
  }[];
};

const MarketingWaitingCheck = () => {
  const { id } = useParams();
  const { data, loading, refetch } = useGetScreenWaiting(id || '');

  const [form] = Form.useForm<WaitingMediaFormValues>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data?.length) {
      form.setFieldsValue({
        media: data.map((item) => ({
          id: item.id,
          url: item.url || '',
        })),
      });
    } else {
      form.setFieldsValue({
        media: [{ url: '' }],
      });
    }
  }, [data, form]);

  const handleReset = () => {
    form.setFieldsValue({
      media: data?.length
        ? data.map((item) => ({
            id: item.id,
            url: item.url || '',
          }))
        : [{ url: '' }],
    });
  };

  const handleSubmit = async (values: WaitingMediaFormValues) => {
    if (!id) {
      message.error('Unable to identify the waiting screen.');
      return;
    }

    const mediaList = values.media || [];

    if (!mediaList.length) {
      message.error('Please add at least one media URL.');
      return;
    }

    try {
      setIsLoading(true);

      const body = mediaList.map((item, index) => {
        const url = item.url.trim();

        return {
          id: item.id || undefined,
          kindType: /\.mp4(?:$|[?#])/i.test(url)
            ? MediaKindType.VIDEO
            : MediaKindType.IMAGE,
          url,
          orderNumber: index + 1,
        };
      });

      const res = await accountApis.updateThemeCheckInWaiting(id, body);

      if (res.status === 200) {
        message.success('Media URLs updated successfully!');
        await refetch();
      } else {
        message.error('Failed to update media URLs.');
      }
    } catch (error) {
      console.error('Error updating media URLs:', error);
      message.error('Unable to update media URLs. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Title>Waiting Screen Media</Title>
          <Subtitle>
            Manage the shared media displayed on marketing waiting screens.
          </Subtitle>
        </HeaderContent>
      </Header>

      <Spin spinning={!!loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.List
            name="media"
            rules={[
              {
                validator: async (_, fields) => {
                  if (!fields || fields.length < 1) {
                    throw new Error('Please add at least one media URL.');
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove, move }, { errors }) => (
              <Wrapper>
                <MediaHeader>
                  <Text variant="CONTENT_2">
                    Image or Video URLs
                  </Text>

                  <AddButton
                    type="button"
                    onClick={() => add({ url: '' })}
                  >
                    <PlusOutlined />
                    Add URL
                  </AddButton>
                </MediaHeader>

                {fields.map((field, index) => (
                  <MediaItem key={field.key}>
                    <IndexLabel>{index + 1}</IndexLabel>

                    <InputWrapper>
                      <Form.Item
                        {...field}
                        name={[field.name, 'url']}
                        label={undefined}
                        rules={[
                          {
                            required: true,
                            whitespace: true,
                            message: 'Please enter an image or video URL.',
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          placeholder="https://example.com/image.jpg or video.mp4"
                          allowClear
                        />
                      </Form.Item>
                    </InputWrapper>

                      <DeleteIconButton
                        type="button"
                        title="Remove URL"
                        onClick={() => remove(field.name)}
                      >
                        <DeleteOutlined/>
                      </DeleteIconButton>
                  </MediaItem>
                ))}

                <Form.ErrorList errors={errors} />

                <Row width="300px" style={{ marginTop: '16px', gap: '16px' }}>
                  <ResetButton
                    type="button"
                    onClick={handleReset}
                    disabled={isLoading}
                  >
                    Reset
                  </ResetButton>

                  <SubmitButton
                    type="submit"
                    disabled={isLoading || !!loading}
                  >
                    OK
                  </SubmitButton>
                </Row>
              </Wrapper>
            )}
          </Form.List>
        </Form>
      </Spin>
    </Container>
  );
};

export default MarketingWaitingCheck;

const Container = styled.div`
  width: 100%;
  font-family: Poppins;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h2`
  margin: 0;
  color: #1d2129;
  font-size: 28px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #667085;
  font-size: 16px;
  line-height: 20px;
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: stretch;

  .ant-input {
    height: 40px;
    border-radius: 6px;
    border-color: #d9dee8;
    font-family: Poppins;
    font-size: 14px;

    &:hover,
    &:focus {
      border-color: #1677ff;
    }
  }
`;

const MediaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const MediaItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
`;

const IndexLabel = styled.div`
  width:40px;
  height: 50px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #f2f4f7;
  color: #475467;
  font-size: 16px;
  font-weight: 600;
`;

const InputWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 12px;
  border: 1px solid ${colorTheme.primary_active};
  border-radius: 8px;
  background: #fff;
  color: ${colorTheme.primary_active};
  font-family: Poppins;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #fff0f2;
  }
`;

const DeleteIconButton = styled.button`
  font-size: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  color: #d92d20;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #fff0f2;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const BaseButton = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: Poppins;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ResetButton = styled(BaseButton)`
  border: 1px solid #d0d5dd;
  background: #fff;
  color: #344054;

  &:hover:not(:disabled) {
    border-color: ${colorTheme.primary_active};
    color: ${colorTheme.primary_active};
  }
`;

const SubmitButton = styled(BaseButton)`
  border: 1px solid ${colorTheme.primary_active};
  background: ${colorTheme.primary_active};
  color: #fff;

  &:hover:not(:disabled) {
    filter: brightness(0.95);
  }
`;